import {captureException} from '@sentry/browser';

import EngComponent from 'constants/EngComponent';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import * as net from 'util/Net';

import {FailedTaskMetadata, FailedTaskErrorResponse} from './TaskRunner';

const ENABLED_COMPONENTS_FOR_FRONTEND_CORRELATION = [EngComponent.MOLBIO];

type FailedTaskObject = FailedTaskErrorResponse & FailedTaskMetadata;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function isFailedTaskObject(res: any): res is FailedTaskObject {
  return Boolean(res.isBenchlingTaskError && res.status);
}

function doesFailedTaskHaveBackendComplement(res: FailedTaskObject): boolean {
  // In Celery tasks, any exceptions that inherit from ClientError are not sent to Sentry, so there's no
  // point in raising them from the frontend for purposes of correlation. The only way to test for non-Client
  // errors is by checking for the generic "unexpected error" message.
  return res.status >= 500 && res.responseText === GENERIC_ERROR_MESSAGE;
}

export function maybeLogErrorResponseToSentry(res: unknown): void {
  if (net.isJqXHR(res) && res.status >= 500) {
    const engComponent = res.getResponseHeader('X-Eng-Component');
    const reqCorrelationId = res.getResponseHeader('X-Debug-Req-Correlation-ID');
    if (
      engComponent &&
      ENABLED_COMPONENTS_FOR_FRONTEND_CORRELATION.includes(engComponent as EngComponent) &&
      reqCorrelationId
    ) {
      const message = res.responseJSON?.devMessage ?? res.responseJSON?.userMessage ?? res.responseText;
      captureException(`Server [${res.status}]: ${message}`, {
        level: 'info',
        tags: {req_correlation_id: reqCorrelationId},
      });
    }
  } else if (
    isFailedTaskObject(res) &&
    doesFailedTaskHaveBackendComplement(res) &&
    ENABLED_COMPONENTS_FOR_FRONTEND_CORRELATION.includes(res.engComponent as EngComponent) &&
    res.initialReqCorrelationId
  ) {
    captureException(`Unexpected task failure in ${res.taskURL}`, {
      level: 'info',
      tags: {req_correlation_id: res.initialReqCorrelationId},
    });
  }
}
